import { useEffect, useId, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import type { TPerson } from '@schemas'
import { useUpdatePersonMembership } from '@/api/hooks'
import { PersonParticipationHistory } from '@/components/people/person-participation'
import { parseApiErrorMessage } from '@/lib/api-errors'
import { toast } from 'sonner'

function formatRelativeDate(value?: string | null) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

export type PersonDetailSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  person: TPerson | null
}

export function PersonDetailSheet({ open, onOpenChange, person }: PersonDetailSheetProps) {
  const [localPerson, setLocalPerson] = useState<TPerson | null>(person)
  useEffect(() => {
    setLocalPerson(person)
  }, [person])

  const activePerson = localPerson ?? person
  const updateMembership = useUpdatePersonMembership()
  const membershipFieldId = useId()
  const judgeFieldId = useId()
  const participationHeadingId = useId()

  const updatedDisplay = formatRelativeDate(activePerson?.updatedAt ?? null)
  const membershipPending = updateMembership.isPending

  const handleMembershipToggle = async (next: boolean) => {
    const current = activePerson
    if (!current?.id) return
    const previous = current
    const desiredJudge = next ? (previous.ahstcJudge ?? false) : false
    setLocalPerson({ ...previous, ahstcMember: next, ahstcJudge: desiredJudge })
    try {
      const updated = await updateMembership.mutateAsync({
        personId: current.id,
        ahstcMember: next,
        ahstcJudge: desiredJudge,
      })
      setLocalPerson({
        ...updated,
        showsEntered: previous.showsEntered,
        ahstcJudge: desiredJudge,
      })
      toast.success(next ? 'Marked as AHSTC member.' : 'Marked as guest.')
    } catch (error) {
      setLocalPerson(previous)
      toast.error(parseApiErrorMessage(error, 'Unable to update membership'))
    }
  }

  const handleJudgeToggle = async (next: boolean) => {
    const current = activePerson
    if (!current?.id || !current.ahstcMember) return
    const previous = current
    setLocalPerson({ ...previous, ahstcJudge: next })
    try {
      const updated = await updateMembership.mutateAsync({
        personId: current.id,
        ahstcJudge: next,
      })
      setLocalPerson({
        ...updated,
        showsEntered: previous.showsEntered,
        ahstcJudge: next,
        ahstcMember: updated.ahstcMember ?? previous.ahstcMember,
      })
      toast.success(next ? 'Marked as AHSTC judge.' : 'Removed judge designation.')
    } catch (error) {
      setLocalPerson(previous)
      toast.error(parseApiErrorMessage(error, 'Unable to update membership'))
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader className="space-y-1">
          <SheetTitle>{activePerson?.name ?? 'Person details'}</SheetTitle>
          <SheetDescription>
            Review contact and membership details, and participation across shows.
          </SheetDescription>
        </SheetHeader>

        {!activePerson ? (
          <div className="px-4 text-sm text-muted-foreground">
            Select a person to preview details.
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-6">
            <div className="space-y-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Contact information</p>
                <div className="rounded-md border bg-muted/40 p-3 text-sm">
                  <p className="font-medium">{activePerson.name}</p>
                  {activePerson.credentialNumber && (
                    <p>Credential number: {activePerson.credentialNumber}</p>
                  )}
                  <p className="text-muted-foreground">
                    {[activePerson.email, activePerson.phone].filter(Boolean).join(' • ') ||
                      'No contact info'}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {activePerson.ahstcMember ? (
                      <Badge variant="secondary">AHSTC member</Badge>
                    ) : (
                      <>
                        <Badge variant="outline">Guest</Badge>
                        <span>Not marked as an AHSTC member.</span>
                      </>
                    )}
                    {activePerson.ahstcMember && activePerson.ahstcJudge ? (
                      <Badge variant="secondary">Judge</Badge>
                    ) : null}
                    {updatedDisplay ? <span>Updated {updatedDisplay}</span> : null}
                  </div>
                </div>
                <div className="rounded-md border border-dashed bg-muted/40 p-3">
                  <label htmlFor={membershipFieldId} className="flex items-start gap-3">
                    <input
                      id={membershipFieldId}
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border border-input"
                      checked={Boolean(activePerson.ahstcMember)}
                      onChange={event => handleMembershipToggle(event.target.checked)}
                      disabled={!activePerson.id || membershipPending}
                    />
                    <div className="space-y-1">
                      <span className="text-sm font-medium text-foreground">AHSTC member</span>
                      <p className="text-xs text-muted-foreground">
                        Toggle membership status for roster exports and eligibility.
                      </p>
                      {membershipPending ? (
                        <p className="text-xs text-muted-foreground">Saving…</p>
                      ) : null}
                    </div>
                  </label>
                </div>
                {activePerson.ahstcMember ? (
                  <div className="rounded-md border border-dashed bg-muted/40 p-3">
                    <label htmlFor={judgeFieldId} className="flex items-start gap-3">
                      <input
                        id={judgeFieldId}
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border border-input"
                        checked={Boolean(activePerson.ahstcJudge)}
                        onChange={event => handleJudgeToggle(event.target.checked)}
                        disabled={!activePerson.id || membershipPending}
                      />
                      <div className="space-y-1">
                        <span className="text-sm font-medium text-foreground">AHSTC judge</span>
                        <p className="text-xs text-muted-foreground">
                          Designate this member as a judge for AHSTC-hosted shows.
                        </p>
                        {membershipPending ? (
                          <p className="text-xs text-muted-foreground">Saving…</p>
                        ) : null}
                      </div>
                    </label>
                  </div>
                ) : null}
              </div>
            </div>

            <Separator />

            <section className="space-y-3" aria-labelledby={participationHeadingId}>
              <div>
                <h3
                  id={participationHeadingId}
                  className="text-sm font-medium text-muted-foreground"
                >
                  Show participation
                </h3>
                <p className="text-xs text-muted-foreground">
                  Staff roles, entrant registration and entries in the shows you can access. Open a
                  show to list its entries.
                </p>
              </div>
              {activePerson.id ? <PersonParticipationHistory personId={activePerson.id} /> : null}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
